import { type Ref, type ComputedRef, computed } from "vue";
import { isNotAuditor } from "./teams";
import { useFabriqStore } from "@/store/fabriq";
import { useTeamsStore } from "@/store/teams";
import { useEventTypesStore } from "@/store/eventTypes";
import { useClientStore } from "@/store/client";
import { useEventsWithPropertiesStore } from "@/store/eventsWithProperties";
import { getAvailableZonesForTeams } from "@/store/eventFilters";
import {
  EventType,
  EventTypeProperty,
  FabriqEventWithReadableProperties,
  Field,
  Group,
  Team,
} from "@/types";
import {
  buildPermission,
  PermissionObject,
} from "@/composables/permissions/permissions";
import { getUserRole } from "@/utils/user-rights";

type EventPermissions = {
  createEventForEventType(eventType: EventType): boolean;
  editEvent(eventId: string): boolean;
};

export function useEventPermissions(): PermissionObject<
  "event",
  EventPermissions,
  false
> {
  const fabriqStore = useFabriqStore();
  const clientStore = useClientStore();
  const eventTypesStore = useEventTypesStore();
  const eventsWithPropertiesStore = useEventsWithPropertiesStore();
  const currentUserId = computed<number | undefined>(
    () => fabriqStore.user?.id
  );
  const currentFutureUserId = computed<string | undefined>(
    () => fabriqStore.user?.future_user_id
  );
  const teamsStore = useTeamsStore();

  const teams: ComputedRef<Team[]> = computed(() => {
    if (!teamsStore.collection?.length) return [];
    return teamsStore.collection.map((t: Team) => {
      return {
        ...t,
        color: t.config?.color,
        icon: t.config?.icon,
      };
    });
  });

  const availableZones: Ref<Field[]> = computed(
    getAvailableZonesForTeams(
      teams,
      clientStore,
      fabriqStore.locale,
      fabriqStore.user
    )
  );

  const eventTypeMap = computed(
    () =>
      new Map<string, EventType>(
        eventTypesStore.collection.map((et: EventType) => [et.id, et])
      )
  );

  const eventMap = computed(
    () =>
      new Map<string, FabriqEventWithReadableProperties>(
        eventsWithPropertiesStore.eventsWithProperties.map((e) => [e.id, e])
      )
  );

  function extractZoneIdFromEvent(
    event: FabriqEventWithReadableProperties,
    eventType: EventType
  ): number | null {
    const ZONE_PROPERTY_TYPE = "zone";
    const zoneProperty = eventType.properties.find(
      (p: EventTypeProperty) => p.type === ZONE_PROPERTY_TYPE
    );
    if (!zoneProperty) return null;

    const matchingProperty = event.properties.find(
      (p) => p.eventPropertyTypeId === zoneProperty.id
    );
    return matchingProperty ? Number(matchingProperty.value) : null;
  }

  function canCreateEventForEventType(eventType: EventType) {
    if (fabriqStore.user?.is_client_admin) return true;

    if (!currentUserId.value) return false;
    const userId = currentUserId.value;
    return canUserInteractWithEventType(eventType, userId);
  }

  function isUserNotAuditorOnAtLeastOneTeamOfSite(
    siteId: number,
    userId: number
  ): boolean {
    const site: Group | undefined = clientStore.groups.find(
      (g) => g.id === siteId
    );
    if (!site) return false;
    if (site.admins.includes(userId)) return true;

    const teamsForSite: Team[] = teamsStore.teamsForCurrentUser.filter(
      (team: Team) => team.groups.includes(siteId)
    );
    return teamsForSite.some((t) => isNotAuditor(t, userId));
  }

  function canUserInteractWithEventType(eventType: EventType, userId: number) {
    const currentEventTypeSiteIds = eventType.groupIds;

    if (currentEventTypeSiteIds.length === 0) {
      const teamsForSite: Team[] = teamsStore.teamsForCurrentUser;
      return teamsForSite.some((t) => isNotAuditor(t, userId));
    }

    return currentEventTypeSiteIds.some((siteId) =>
      isUserNotAuditorOnAtLeastOneTeamOfSite(siteId, userId)
    );
  }

  function hasSufficientRoleForEventType(
    eventType: EventType,
    userId: number,
    allowedRoles: string[]
  ): boolean {
    // no sites for an eventType means it's available for all sites
    const currentSites: Group[] = (
      eventType.groupIds.length > 0
        ? eventType.groupIds
            .map((groupId) =>
              clientStore.groups.find((group: Group) => group.id === groupId)
            )
            .filter(Boolean)
        : clientStore.groups
    ) as Group[];

    const currentEventTypeSiteIds = currentSites.map((site) => site.id);

    if (currentSites.some((site) => site.admins.includes(userId))) {
      return true;
    }

    return currentEventTypeSiteIds.some((siteId) => {
      const teamsForSite: Team[] = teamsStore.teamsForCurrentUser.filter(
        (team: Team) => team.groups.includes(siteId)
      );

      const roleFound = teamsForSite.some((team: Team) => {
        const role = getUserRole(team, userId, clientStore.tmpNewTeamRoles);
        if (!role) return false;
        return allowedRoles.includes(role);
      });

      if (roleFound) {
        return true;
      }

      return false;
    });
  }

  function canEditEvent(eventId: string): boolean {
    if (fabriqStore.user?.is_client_admin) return true;

    const event = eventMap.value.get(eventId);

    if (!event) return false;

    const userId = currentUserId.value;
    if (!userId) return false;

    const futureUserId = currentFutureUserId.value;
    if (!futureUserId) return false;

    if (event.createdBy === futureUserId) return true;

    const allowedRolesForEdit = ["siteAdmin", "admin", "owner", "coordinator"];

    const eventType = eventTypeMap.value.get(event.eventTypeId);
    if (!eventType) return false;

    const hasSufficientRole = hasSufficientRoleForEventType(
      eventType,
      userId,
      allowedRolesForEdit
    );

    if (!hasSufficientRole) return false;

    const zoneId = extractZoneIdFromEvent(event, eventType);
    if (!zoneId) return false;
    return availableZones.value.some((z) => z.id === zoneId);
  }

  return buildPermission("event", Boolean(), {
    createEventForEventType: canCreateEventForEventType,
    editEvent: canEditEvent,
  });
}
